import React from 'react';
import { useLocation } from 'react-router-dom';
import ServiceGrid from '../components/ServiceProvider/ServiceGrid';
import ServiceDiscoveryControls from '../components/ServiceProvider/ServiceDiscoveryControls';
import {
  PageEmptyState,
  PageErrorState,
  PageNoResultsState,
} from '../components/common/ViewStates';
import { useServiceDiscovery } from '../hooks/useServiceDiscovery';

const ServicesListPage: React.FC = () => {
  const location = useLocation();
  const isAuthenticatedRoute = location.pathname.startsWith('/app');

  const {
    services,
    sortedServices,
    uniqueCities,
    loading,
    error,
    query,
    city,
    sortBy,
    issueType,
    smartSelection,
    hourlyRateMax,
    includeUnpriced,
    searchState,
    setSortFilter,
    setCityFilter,
    setIssueTypeFilter,
    setHourlyRateMaxFilter,
    setIncludeUnpricedFilter,
    setQueryFromSearch,
    clearHourlyRateFilter,
    resetFilters,
    refresh,
    searchParams,
  } = useServiceDiscovery();

  const params = searchParams.toString();
  const mapUrl = `${isAuthenticatedRoute ? '/app/discover' : '/map'}${params ? `?${params}` : ''}`;
  const listUrl = `${isAuthenticatedRoute ? '/app/list' : '/list'}${params ? `?${params}` : ''}`;

  return (
    <div className="rs-page-gutter pb-0 pt-1.5 md:pt-3">
      <div className="rs-wide-monitor-container mx-auto w-full space-y-2.5 md:space-y-3.5">
        <div className="mx-auto w-full max-w-6xl">
          <ServiceDiscoveryControls
            activeView="list"
            mapUrl={mapUrl}
            listUrl={listUrl}
            resultsCount={searchState.filteredCount}
            resultsCountLoading={loading}
            query={query}
            city={city}
            sortBy={sortBy}
            issueType={issueType}
            smartSelection={smartSelection}
            hourlyRateMax={hourlyRateMax}
            includeUnpriced={includeUnpriced}
            cities={uniqueCities}
            services={services}
            disabled={loading}
            onSearchChange={setQueryFromSearch}
            onCityChange={setCityFilter}
            onSortChange={setSortFilter}
            onIssueTypeChange={setIssueTypeFilter}
            onHourlyRateMaxChange={setHourlyRateMaxFilter}
            onIncludeUnpricedChange={setIncludeUnpricedFilter}
            onResetFilters={resetFilters}
          />
        </div>

        {loading && <ServiceGrid services={[]} isLoading />}

        {error && !loading && (
          <div className="mx-auto w-full max-w-6xl">
            <PageErrorState
              title="Nie udało się pobrać serwisów"
              message={error}
              actionLabel="Spróbuj ponownie"
              onAction={refresh}
            />
          </div>
        )}

        {!loading && !error && searchState.totalCount === 0 && (
          <div className="mx-auto w-full max-w-6xl">
            <PageEmptyState
              title="Brak serwisów do wyświetlenia"
              message="Gdy pojawią się aktywne profile serwisowe, zobaczysz je tutaj."
            />
          </div>
        )}

        {!loading && !error && searchState.totalCount > 0 && searchState.filteredCount === 0 && (
          <div className="mx-auto w-full max-w-6xl">
            <PageNoResultsState
              title={
                searchState.isHourlyRateNoResults
                  ? 'Nie znaleźliśmy serwisów z taką stawką.'
                  : searchState.isSmartNoResults
                    ? 'Nie znaleźliśmy specjalisty dla tej sprawy.'
                    : 'Brak wyników wyszukiwania'
              }
              message={
                searchState.isHourlyRateNoResults
                  ? 'Zwiększ limit albo uwzględnij serwisy bez podanej stawki.'
                  : searchState.isSmartNoResults
                    ? 'Wybierz szerszy obszar, zmień filtry albo zgłoś brakującą usługę.'
                    : 'Zmień frazę lub filtry, aby zobaczyć więcej serwisów.'
              }
              actionLabel={searchState.isHourlyRateNoResults ? 'Wyczyść stawkę' : 'Wyczyść filtry'}
              onAction={searchState.isHourlyRateNoResults ? clearHourlyRateFilter : resetFilters}
            />
          </div>
        )}

        {!loading && !error && searchState.filteredCount > 0 && (
          <ServiceGrid
            services={sortedServices}
            isLoading={loading}
            query={query}
            smartSelection={smartSelection}
            issueType={issueType}
          />
        )}
      </div>
    </div>
  );
};

export default ServicesListPage;
